import { v4 as uuidv4 } from "uuid";

const FACILITY_TZ = "Africa/Dakar";

export const generateUUID = () => {
  return uuidv4();
};

export const convertToDateAndTime = (dateString: string) => {
  const date = new Date(dateString);
  const dateOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: FACILITY_TZ,
  } as const;
  const formattedDate = date.toLocaleDateString("fr-FR", dateOptions);
  const formattedTime = date.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: FACILITY_TZ,
  });
  return { date: formattedDate, time: formattedTime };
};

export const convertToDateISOString = (dateString: string) => {
  // Format date of birth to the required format: 2004-03-22T00:00:00Z
  const date = new Date(dateString);
  const formattedDate = date.toISOString();
  return formattedDate;
};

export const formatDateForInput = (dateString: string | undefined): string => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

export const getDateOfWeek = (dateString: string): string | null => {
  if (!dateString) return null;

  // Parse the date string as local date to avoid timezone issues
  // Input format: "YYYY-MM-DD"
  const parts = dateString.split("-");
  if (parts.length !== 3) return null;

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
  const day = parseInt(parts[2], 10);

  const date = new Date(year, month, day);
  if (isNaN(date.getTime())) return null;

  const dayIndex = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const dayMapping: Record<number, string> = {
    0: "SUNDAY",
    1: "MONDAY",
    2: "TUESDAY",
    3: "WEDNESDAY",
    4: "THURSDAY",
    5: "FRIDAY",
    6: "SATURDAY",
  };
  return dayMapping[dayIndex];
};

/**
 * MED-1653: a birth date typed in a date field ("YYYY-MM-DD"): a real calendar day with a 4-digit
 * year, not in the future and at most 120 years ago. The date input accepted "51519-09-01".
 */
export const isPlausibleBirthDate = (value: string): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value ?? "").trim());
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return false;
  }
  const today = new Date();
  const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return date.getTime() <= todayUtc && year >= today.getFullYear() - 120;
};
